import React, { useState } from "react";
import { toast } from "sonner";
import { Globe, Loader2, X, Send } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * "Know this campsite's website? Send it →" button shown on detail
 * pages where we have no `website` URL on file (~6,500 OSM-imported
 * sites). Camper submits the official URL → admin reviews + applies in
 * /admin → Website Reports.
 *
 * Anon users get a soft nudge to sign up (prevents abuse + lets us
 * follow up with them when their submission goes live).
 */
export default function ReportWebsiteButton({ campsiteId, campsiteName }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u || u.length < 4 || !u.includes(".")) {
      toast.error("Paste a valid website URL");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/campsites/${campsiteId}/report-website`, {
        url: u,
        note: note.trim() || null,
      });
      toast.success("Thanks! We'll check the link and add it soon.");
      setDone(true);
      setTimeout(() => {
        setOpen(false);
        setUrl("");
        setNote("");
        setDone(false);
      }, 1400);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  // Logged-out — nudge to signup, preserving return path so they land
  // back here ready to submit.
  if (!user) {
    return (
      <button
        type="button"
        onClick={() => {
          toast("Sign up to share this campsite's website — takes 20 seconds.", { icon: "🔐" });
          const next = encodeURIComponent(location.pathname + location.search);
          setTimeout(() => navigate(`/register?next=${next}`), 250);
        }}
        className="w-full mt-3 inline-flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-[#cfcfcf] text-[12px] mono uppercase tracking-wider text-[#666] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors"
        data-testid="report-website-cta-anon"
      >
        <Globe className="w-3.5 h-3.5" />
        Know this site's website? Send it →
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full mt-3 inline-flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-[#cfcfcf] text-[12px] mono uppercase tracking-wider text-[#666] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors"
        data-testid="report-website-btn"
      >
        <Globe className="w-3.5 h-3.5" />
        Know this site's website? Send it →
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="report-website-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-white max-w-md w-full p-6 border-2 border-[#ff4500] shadow-[0_8px_0_0_#111]"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="eyebrow text-[#ff4500]">Help every camper</div>
                <h4 className="font-display text-2xl mt-1">Share the website</h4>
                {campsiteName && (
                  <div className="text-xs text-[#666] mono mt-1 break-words">
                    {campsiteName}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                className="p-2 hover:bg-[#fafafa]"
                aria-label="Close"
                data-testid="report-website-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-[#555] leading-relaxed mb-4">
              We don't have an official website on file for this campsite.
              If you know one, paste it below. Multiple submissions get
              priority and we'll verify before adding.
            </p>

            <label className="block mb-3">
              <span className="eyebrow block mb-1.5">Website URL</span>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="campsitename.co.uk"
                inputMode="url"
                autoComplete="url"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                required
                disabled={busy || done}
                className="w-full border hairline px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff4500] mono"
                data-testid="report-website-input"
              />
            </label>

            <label className="block mb-4">
              <span className="eyebrow block mb-1.5">Note (optional)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 300))}
                placeholder="e.g. Owner posted it on their Facebook page"
                rows={2}
                maxLength={300}
                disabled={busy || done}
                className="w-full border hairline px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff4500] resize-none"
                data-testid="report-website-note"
              />
              <span className="text-[10px] mono text-[#888] block mt-1 text-right">{note.length} / 300</span>
            </label>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                className="text-xs mono uppercase tracking-wider px-4 py-2 border hairline hover:bg-[#fafafa]"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || done}
                className="btn-primary text-xs px-5 py-2 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="report-website-submit"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {!busy && !done && <Send className="w-3.5 h-3.5" />}
                {done ? "Sent!" : "Send to admin"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
